// Shared constants/helpers for the MCP (AI trading) add-on.
import { config } from '../env';
import { ADDON_PRODUCTS, BILLING_CYCLES } from '../lib/billing/config';
import { resolveCatalogPrice, computeLineAmountCents } from '../lib/billing/priceCatalog';

// The one role that grants the MCP add-on. Provisioned by billing; until a
// user has it, the MCP surfaces show the locked / buy screen. The token's
// roles claim carries Auth0 role DISPLAY names (see Auth0Context:
// 'Dedicated Env', 'Overdue Subscription') — this must match the
// Terraform-managed role exactly (terraform-site/auth0/dev/role_mcp_user.tf).
export const MCP_ROLE = 'MCP User';

// Public MCP connector URL users paste into Claude / their MCP client.
//
// Sourced from src/env alongside envoy_hostname, but every environment now
// points at production (mcp.tradersconnect.com). This value is only ever
// DISPLAYED and copied — the web app never calls the MCP server, the user's AI
// client does — and the docs it feeds are customer instructions, so a dev or
// staging build showing mcp.staging.* meant screenshots and copied commands
// sent people at the wrong tenant. To exercise the staging MCP server, point a
// client at mcp.staging.tradersconnect.com/mcp by hand rather than reading it
// out of this app.
export const MCP_CONNECTOR_URL: string = config.mcp_connector_url;

// Client ID Metadata Document (CIMD) URL for the command-line MCP clients
// (OpenClaw, Hermes). Our Auth0 tenant has Dynamic Client Registration
// disabled, so a CLI that tries to register itself never gets past
// registration. Pointing it at this document skips registration entirely —
// under CIMD the client_id IS the URL the document is served from. Claude and
// ChatGPT never need it: they are registered from their own vendor-hosted
// documents.
//
// Derived from the connector URL so the two can never drift apart across
// environments (the server builds it from the same public base). Mirrors
// mcp-server/internal/auth/clientmetadata.go — CLIClientMetadataPath is
// '/clients/cli' and is FROZEN once the Auth0 application exists: renaming it
// invalidates the registration and every configured client. Update BOTH.
export const MCP_CLI_CLIENT_METADATA_URL: string = `${MCP_CONNECTOR_URL.replace(/\/mcp\/?$/, '')}/clients/cli`;

// Local name for our server inside a CLI client's own config. Purely
// client-side — never sent to us, meaningless to Auth0 — but it has to be
// identical across the client's add / login / list / test commands, so it lives
// in one place. Deliberately environment-neutral: the procedure was first
// written down with a 'tc-staging' alias, which must never reach a customer.
export const MCP_CLI_SERVER_ALIAS = 'tradersconnect';

// The only callbacks our sign-in accepts, byte for byte. Auth0 matches redirect
// URIs exactly — no host, port or path wildcards — so a client on the right
// port but a different path is refused at /authorize with no useful error.
// Identical in every environment: these are loopback URLs on the user's own
// machine. Mirrors mcp-server/internal/auth/clientmetadata.go, whose test
// asserts the list is exactly these four — update BOTH, and re-import the
// document into Auth0.
export const MCP_CLI_REDIRECT_URIS = [
  'http://127.0.0.1:8989/oauth/callback',
  'http://localhost:8989/oauth/callback',
  'http://127.0.0.1:8989/callback',
  'http://localhost:8989/callback'
] as const;

// Platforms AI Connect can trade today. Values are the account-manager
// platform strings (connector-lib: PlatformMT4 = "MT-4", PlatformMT5 = "MT-5").
// Every AI-Connect account surface (dashboard list, activity filter) narrows to
// these — arming a cTrader/TradeLocker/etc. account would do nothing since the
// AI can only reach MetaTrader. Widen this as more platforms are supported.
export const MCP_SUPPORTED_PLATFORMS = ['MT-4', 'MT-5'] as const;

// isMcpPlatform reports whether an account's platform is AI-Connect-eligible.
export const isMcpPlatform = (platform?: string): boolean =>
  !!platform && (MCP_SUPPORTED_PLATFORMS as readonly string[]).includes(platform);

// Display fallback prices in DOLLARS (contract). Real amounts come from listPrices.
export const MCP_PRICE_MONTHLY = 50;

// Per-cadence offline fallback amounts in CENTS. Used ONLY until listPrices
// populates the MCP catalog. Mirrors billing-manager's Stripe prices exactly.
// One package for launch: monthly.
export const MCP_FALLBACK_CENTS = {
  monthly: 5000
} as const;

// Minimal structural shape of the price catalog produced by
// lib/billing/priceCatalog buildPriceCatalog (same surface as checkout-v2's
// BillingCatalog). Declared locally so this shared util stays decoupled from
// the checkout feature's store types — buildMcpCadences only hands the catalog
// to resolveCatalogPrice.
export type McpPriceCatalog = {
  raw: unknown;
  get: (product: unknown, cycle: unknown, variant?: unknown) => unknown;
  has: (product: unknown, cycle: unknown, variant?: unknown) => boolean;
  getById: (id: unknown) => unknown;
  listForProduct: (product: unknown) => unknown[];
};

export type McpCadence = {
  cycle: string;
  label: string;
  // Stripe recurring interval + interval_count for this cadence. Fed to
  // intervalPriceLabel so the cadence card's "per <cadence>" suffix is derived
  // (month/3 → per quarter, month/6 → per 6 months) rather than hardcoded.
  interval: string;
  intervalCount: number;
  note?: string;
  priceId: string | null;
  amountCents: number;
};

// Static cadence metadata (order = display order). Prices are filled by
// buildMcpCadences from the live catalog, or the contract fallback. One
// package for launch: monthly — the catalog may carry other cadences, but the
// UI only offers this one.
const MCP_CADENCE_META: { cycle: string; label: string; interval: string; intervalCount: number }[] = [
  { cycle: BILLING_CYCLES.monthly, label: 'Monthly', interval: 'month', intervalCount: 1 }
];

// Resolve the offered MCP cadence card(s) from the billing catalog. Each card
// gets the live Stripe price id + amount when the catalog has an mcp_<cycle>
// price, else the offline contract fallback (priceId null, contract cents).
export const buildMcpCadences = (catalog: McpPriceCatalog | null): McpCadence[] =>
  MCP_CADENCE_META.map((c) => {
    const price = catalog ? resolveCatalogPrice(catalog, ADDON_PRODUCTS.mcp, c.cycle, {}) : null;
    const amountCents = price?.id ? computeLineAmountCents(price, 1) : MCP_FALLBACK_CENTS[c.cycle as keyof typeof MCP_FALLBACK_CENTS];
    return { ...c, priceId: price?.id ?? null, amountCents };
  });

// hasMcpAccess reports whether the user is entitled to MCP. Strictly the
// "MCP User" role — no admin bypass, no aliases: everyone without the role
// (admins included) sees the locked intro and gets access the same way
// customers do.
export const hasMcpAccess = (roles?: string[]): boolean => !!roles?.includes(MCP_ROLE);

// canSeeMcp reports whether the AI Connect surfaces should be RENDERED AT ALL.
//
// CONTROLLED ROLLOUT — remove at public launch. Until then AI Connect is
// concealed from everyone without the MCP User role: no sidebar item, no billing
// card, no locked intro, and /ai-connect redirects away. Testers get access by
// being granted the role in Auth0, exactly as a paying customer would.
//
// At launch, delete this helper and its call sites (grep canSeeMcp — sidebar,
// checkout add-ons, McpHubPage, McpDocsPage, McpSection). The surfaces then fall
// back to their own hasMcpAccess checks, which show non-entitled users the
// locked intro and the buy flow.
export const canSeeMcp = (roles?: string[]): boolean => hasMcpAccess(roles);

// MCP_PANEL_SX is the single panel recipe for every MCP surface: the Dashboard's
// 20px rounded card + border.main outline, plus Signal Desk's 1px inset
// top-highlight ("card stock catching light"). Custom panels and TableWrapper
// panels share it so stacked cards on one page never disagree.
export const MCP_PANEL_SX = {
  border: '1px solid',
  borderColor: 'border.main',
  borderRadius: '20px',
  bgcolor: 'background.paper',
  boxShadow: (theme: { palette: { mode: string } }) =>
    theme.palette.mode === 'dark' ? 'inset 0 1px 0 rgba(255,255,255,0.03)' : 'inset 0 1px 0 rgba(255,255,255,0.6)'
} as const;

// One real monospace stack for account ids and the connector URL — the bare
// 'monospace' keyword resolves to Courier on some platforms and clashes with Inter.
export const MCP_MONO = "ui-monospace, 'SF Mono', 'Cascadia Mono', 'Roboto Mono', Menlo, monospace";
